import type { ExpressionSpecification, FilterSpecification } from 'maplibre-gl'
import { useMemo } from 'react'
import {
  AVERTED_DAMAGES_BREAKS,
  BUILDING_DAMAGES_BREAKS,
  BUILDING_SOURCES,
  COLORMAP_PURPLE_BENEFITS,
  COLORMAP_RED,
  COSMOS_SITE_ADAPTATION_CODES,
  EROSION_CONTEXT_COLORS,
  VisibleLayers,
  buildingLayerId,
  cosmosSiteForBuildingSource,
  type AdaptationCode,
} from '~/constants'
import { useAppStore, usePMTiles } from '~/hooks'
import { Layer, Source } from 'react-map-gl/maplibre'

function damageSumExpression(structureKey: string, contentsKey: string): ExpressionSpecification {
  return [
    '+',
    ['coalesce', ['get', structureKey], 0],
    ['coalesce', ['get', contentsKey], 0],
  ] as ExpressionSpecification
}

function interpolateDamages(
  valueExpr: ExpressionSpecification,
  colors: readonly string[],
  breaks: readonly number[]
): ExpressionSpecification {
  return [
    'interpolate',
    ['linear'],
    valueExpr,
    ...breaks.flatMap((breakpoint, i) => [breakpoint, colors[i]!]),
  ] as ExpressionSpecification
}

function paintForSite(
  dem: string,
  returnPeriod: string,
  adaptation: string,
  showBenefits: boolean
): { fillColor: ExpressionSpecification; filter: FilterSpecification } {
  const structureKey = (adapt: string) =>
    `structure_damage_value_dem${dem}_rp${returnPeriod}_adapt${adapt}`
  const contentsKey = (adapt: string) =>
    `contents_damage_value_dem${dem}_rp${returnPeriod}_adapt${adapt}`

  if (showBenefits) {
    const baselineSum = damageSumExpression(structureKey('000'), contentsKey('000'))
    const adaptedSum = damageSumExpression(structureKey(adaptation), contentsKey(adaptation))
    const avertedExpr = ['max', 0, ['-', baselineSum, adaptedSum]] as ExpressionSpecification
    return {
      fillColor: interpolateDamages(avertedExpr, COLORMAP_PURPLE_BENEFITS, AVERTED_DAMAGES_BREAKS),
      filter: ['>', baselineSum, 0] as unknown as FilterSpecification,
    }
  }

  const sum = damageSumExpression(structureKey(adaptation), contentsKey(adaptation))
  return {
    fillColor: interpolateDamages(sum, COLORMAP_RED, BUILDING_DAMAGES_BREAKS),
    filter: ['>', sum, 0] as unknown as FilterSpecification,
  }
}

type BuildingLayerSpec = {
  source: (typeof BUILDING_SOURCES)[number]
  url: string
  sourceLayer: string
  layerId: string
  fillColor: ExpressionSpecification | string
  filter: FilterSpecification
  erosionContext: boolean
}

export function BuildingLayer() {
  const { sources, layerInfo } = usePMTiles()
  const { layerVisibility, dataset, DEM, returnPeriod, adaptation, avertedDamagesEnabled } =
    useAppStore()
  const isErosion = dataset === 'erosion'

  const visibility: 'visible' | 'none' =
    (dataset === 'cosmos' || isErosion) && layerVisibility[VisibleLayers.BUILDING]
      ? 'visible'
      : 'none'

  const layers = useMemo((): BuildingLayerSpec[] => {
    if (!layerInfo) return []

    const result: BuildingLayerSpec[] = []
    for (const source of BUILDING_SOURCES) {
      const url = sources[source]
      const sourceLayer = layerInfo[source]?.[0]?.layer
      if (!url || !sourceLayer) continue

      // Erosion view: every footprint, uncoloured. The damage filter would drop
      // unflooded buildings, which is a flood question, not an erosion one.
      // Mount every site so LayerId.BUILDING (Santa Cruz fill) stays available
      // as ErosionLayer's beforeId.
      if (isErosion) {
        result.push({
          source,
          url,
          sourceLayer,
          layerId: buildingLayerId(source),
          fillColor: EROSION_CONTEXT_COLORS.building,
          filter: true as FilterSpecification,
          erosionContext: true,
        })
        continue
      }

      if (!DEM || !returnPeriod || !adaptation) continue

      const site = cosmosSiteForBuildingSource(source)
      const siteSupports =
        site != null && COSMOS_SITE_ADAPTATION_CODES[site].includes(adaptation as AdaptationCode)
      // No dune data for HMB — hide buildings rather than showing misleading baseline damages.
      if (adaptation !== '000' && !siteSupports) continue

      const showBenefits = adaptation !== '000' && avertedDamagesEnabled
      const { fillColor, filter } = paintForSite(DEM, returnPeriod, adaptation, showBenefits)
      result.push({
        source,
        url,
        sourceLayer,
        layerId: buildingLayerId(source),
        fillColor,
        filter,
        erosionContext: false,
      })
    }
    return result
  }, [DEM, returnPeriod, adaptation, avertedDamagesEnabled, isErosion, layerInfo, sources])

  if (!layers.length) return null

  return (
    <>
      {layers.map(({ source, url, sourceLayer, layerId, fillColor, filter, erosionContext }) => (
        <Source key={source} id={source} type="vector" url={`pmtiles://${url}`}>
          <Layer
            id={layerId}
            type="fill-extrusion"
            source={source}
            source-layer={sourceLayer}
            layout={{ visibility }}
            filter={filter}
            paint={
              erosionContext
                ? {
                    'fill-extrusion-color': EROSION_CONTEXT_COLORS.building,
                    'fill-extrusion-opacity': 0.45,
                    'fill-extrusion-height': 6,
                    'fill-extrusion-base': 0,
                  }
                : {
                    'fill-extrusion-color': fillColor as never,
                    'fill-extrusion-opacity': 0.9,
                    'fill-extrusion-height': 6,
                    'fill-extrusion-base': 0,
                  }
            }
          />
        </Source>
      ))}
    </>
  )
}
